/**
 * Der Anmeldeweg einer Browser-Anwendung gegen den Identitätsanbieter.
 *
 * **Warum hier und nicht im Portal.** Die Monteur-PWA braucht denselben Weg,
 * und zwei Fassungen einer Anmeldung sind ab der ersten Änderung zwei
 * verschiedene Anmeldungen. Die Regeln stehen deshalb neben dem Vertrag, den
 * sie bedienen (`anmeldung.ts`), und nicht in der Anwendung.
 *
 * **Ohne Client-Geheimnis, mit PKCE.** Eine Anwendung im Browser kann nichts
 * geheim halten — was sie mitbringt, steht im ausgelieferten JavaScript.
 * `infra/keycloak/clients.yaml` führt das Portal deshalb als öffentlichen
 * Client, und an die Stelle des Geheimnisses tritt PKCE (RFC 7636): ein
 * Einmalgeheimnis je Anmeldung, von dem nur die Prüfsumme über die Adresszeile
 * geht.
 *
 * **Drei Werte, drei verschiedene Angriffe.** Sie werden gern verwechselt,
 * und jeder einzelne schützt gegen etwas anderes:
 *
 * | Wert | Verhindert |
 * |---|---|
 * | `code_verifier` (PKCE) | Ein abgefangener Autorisierungscode allein nützt nichts |
 * | `state` | Eine untergeschobene Anmeldung — der Rückweg gehört zu **dieser** Anfrage |
 * | `nonce` | Ein andernorts erbeutetes ID-Token wird nicht angenommen |
 *
 * Wer nur PKCE einbaut, hat den Code geschützt und den Rückweg offen gelassen.
 */

import type { IdentitaetsAnbieter } from "./anbieter.js";

/** Was eine Anmeldung im Browser zwischenspeichern muss, bis sie zurückkommt. */
export interface AngefangeneAnmeldung {
  /** PKCE. Bleibt im Browser, geht nie über die Adresszeile. */
  readonly verifizierer: string;
  /** Gegen untergeschobene Rückwege. */
  readonly zustand: string;
  /** Gegen anderswo erbeutete ID-Token. */
  readonly einmalwert: string;
  /** Wohin nach erfolgreicher Anmeldung zurückgesprungen wird. */
  readonly zurueckNach: string;
}

export interface AnmeldeAnfrage {
  readonly url: string;
  readonly angefangen: AngefangeneAnmeldung;
}

/**
 * Zufall, der auch Zufall ist.
 *
 * `Math.random()` steht ausdrücklich nicht zur Wahl: Es ist nicht
 * kryptografisch, und die Werte hier schützen einen Autorisierungscode. In
 * jedem Browser, der diese Anwendung ausliefern darf, gibt es `crypto`.
 */
function zufallstext(bytes = 32): string {
  const roh = new Uint8Array(bytes);
  crypto.getRandomValues(roh);
  return base64url(roh);
}

/** Base64 ohne Polsterung und ohne `+/`, wie RFC 7636 es verlangt. */
function base64url(roh: Uint8Array): string {
  let s = "";
  for (const b of roh) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Die Prüfsumme zum Einmalgeheimnis — `S256`, nicht `plain`.
 *
 * `plain` schickt das Geheimnis selbst über die Adresszeile und schützt damit
 * gegen nichts; Keycloak lässt es zu, wenn man es zulässt. Der Realm-Abgleich
 * setzt deshalb `pkce.code.challenge.method: S256`, und diese Funktion ist die
 * andere Hälfte davon.
 */
export async function pruefsumme(verifizierer: string): Promise<string> {
  const roh = new TextEncoder().encode(verifizierer);
  const summe = await crypto.subtle.digest("SHA-256", roh);
  return base64url(new Uint8Array(summe));
}

/**
 * Baut die Adresse, an die der Browser zur Anmeldung geschickt wird.
 *
 * `zurueckNach` ist der Ort **innerhalb** der Anwendung, an den es nach der
 * Anmeldung weitergeht — nicht die Rücksprungadresse des Clients. Beides zu
 * verwechseln ist der Weg zu einer offenen Weiterleitung: Der Client-Rückweg
 * steht in Keycloak und ist genau eine Adresse, der Ort danach ist ein Pfad
 * dieser Anwendung und wird nie aus einer Adresszeile übernommen.
 */
export async function anmeldeAnfrage(
  anbieter: IdentitaetsAnbieter,
  einstellung: {
    readonly rueckweg: string;
    readonly zurueckNach?: string;
    readonly bereiche?: readonly string[];
  },
): Promise<AnmeldeAnfrage> {
  const verifizierer = zufallstext();
  const zustand = zufallstext(16);
  const einmalwert = zufallstext(16);
  const summe = await pruefsumme(verifizierer);

  const frage = new URLSearchParams({
    client_id: anbieter.empfaenger,
    redirect_uri: einstellung.rueckweg,
    response_type: "code",
    scope: (einstellung.bereiche ?? ["openid", "profile", "email"]).join(" "),
    state: zustand,
    nonce: einmalwert,
    code_challenge: summe,
    code_challenge_method: "S256",
  });

  return {
    url: `${anbieter.aussteller}/protocol/openid-connect/auth?${frage.toString()}`,
    angefangen: {
      verifizierer,
      zustand,
      einmalwert,
      zurueckNach: einstellung.zurueckNach ?? "/",
    },
  };
}

export type Rueckkehr =
  | { readonly art: "code"; readonly code: string; readonly zustand: string }
  | { readonly art: "fehler"; readonly grund: string; readonly zustand?: string }
  | { readonly art: "keine" };

/**
 * Was in der Adresszeile steht, nachdem Keycloak zurückgeschickt hat.
 *
 * **Der Fehlerfall gehört dazu.** Keycloak antwortet auf eine abgelehnte
 * Anmeldung mit `?error=…` und HTTP 200 — wer nur nach `code` sucht, sieht
 * eine Seite ohne Anmeldung und ohne Grund.
 */
export function leseRueckkehr(suche: string): Rueckkehr {
  const p = new URLSearchParams(suche);
  const fehler = p.get("error");
  if (fehler) {
    const beschreibung = p.get("error_description");
    return {
      art: "fehler",
      grund: beschreibung ? `${fehler}: ${beschreibung}` : fehler,
      ...(p.get("state") ? { zustand: p.get("state") as string } : {}),
    };
  }
  const code = p.get("code");
  const zustand = p.get("state");
  if (code && zustand) return { art: "code", code, zustand };
  return { art: "keine" };
}

export interface Tokensatz {
  readonly zugriffstoken: string;
  readonly idToken: string;
  readonly aktualisierungstoken?: string;
  /** Sekunden bis zum Ablauf des Zugriffstokens. */
  readonly gueltigFuer: number;
}

/**
 * Löst den Autorisierungscode gegen Tokens ein.
 *
 * **Der Zustand wird hier geprüft, nicht später.** Stimmt er nicht, ist die
 * Rückkehr nicht die Antwort auf unsere Anfrage — dann wird kein Code
 * eingelöst, sondern abgebrochen. Ein Aufrufer, der das nachholen wollte,
 * hätte den Code bereits verbraucht.
 */
export async function tauscheCode(
  anbieter: IdentitaetsAnbieter,
  angefangen: AngefangeneAnmeldung,
  rueckkehr: Extract<Rueckkehr, { art: "code" }>,
  rueckweg: string,
  holen: typeof fetch = fetch,
): Promise<Tokensatz> {
  if (rueckkehr.zustand !== angefangen.zustand) {
    throw new Error(
      "Der Zustand der Rückkehr gehört nicht zu dieser Anmeldung.",
    );
  }

  const leib = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: anbieter.empfaenger,
    code: rueckkehr.code,
    redirect_uri: rueckweg,
    code_verifier: angefangen.verifizierer,
  });

  const antwort = await holen(
    `${anbieter.aussteller}/protocol/openid-connect/token`,
    {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: leib.toString(),
    },
  );

  if (!antwort.ok) {
    // Der Text der Antwort trägt bei Keycloak eine Begründung, aber auch den
    // Code — der gehört nicht in eine Fehlermeldung, die irgendwo landet.
    throw new Error(`Token-Anfrage abgelehnt (HTTP ${antwort.status}).`);
  }

  const d = (await antwort.json()) as Record<string, unknown>;
  const zugriffstoken = typeof d["access_token"] === "string" ? d["access_token"] : "";
  const idToken = typeof d["id_token"] === "string" ? d["id_token"] : "";
  if (!zugriffstoken || !idToken) {
    throw new Error("Antwort ohne Token.");
  }
  const auffrischen = d["refresh_token"];
  return {
    zugriffstoken,
    idToken,
    ...(typeof auffrischen === "string" ? { aktualisierungstoken: auffrischen } : {}),
    gueltigFuer: typeof d["expires_in"] === "number" ? d["expires_in"] : 0,
  };
}

/**
 * Liest die Ansprüche aus einem ID-Token, **ohne** die Signatur zu prüfen.
 *
 * **Das ist kein Versehen und trotzdem eine Gefahr.** Ein Browser kann die
 * Signatur prüfen, aber es nützt ihm wenig: Wer das JavaScript beeinflusst,
 * beeinflusst auch die Prüfung. Der Schutz liegt woanders — jeder Dienst, der
 * etwas herausgibt, prüft das Token selbst (`01` §1.11: die Berechtigung
 * entscheidet das Quellsystem).
 *
 * Was hier gelesen wird, dient allein der Anzeige: welcher Name in der
 * Kopfzeile steht, welche Menüpunkte erscheinen. Ein verstecktes Menü hat noch
 * nie etwas geschützt, und ein angezeigtes gibt nichts heraus.
 */
export function liesAnsprueche(idToken: string): Record<string, unknown> {
  const teile = idToken.split(".");
  if (teile.length !== 3) throw new Error("Kein JWT.");
  const roh = teile[1] as string;
  const b64 = roh.replace(/-/g, "+").replace(/_/g, "/");
  const gepolstert = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
  return JSON.parse(atob(gepolstert)) as Record<string, unknown>;
}

/**
 * Die Adresse, an die der Browser zum Abmelden geschickt wird.
 *
 * Das lokale Vergessen genügt nicht: Wer nur den Browserzustand löscht, ist
 * beim nächsten Aufruf sofort wieder angemeldet, weil die Sitzung bei Keycloak
 * weiterläuft. Erst dieser Weg beendet sie.
 */
export function abmeldeUrl(
  anbieter: IdentitaetsAnbieter,
  einstellung: { readonly idToken?: string; readonly zurueckNach: string },
): string {
  const frage = new URLSearchParams({
    post_logout_redirect_uri: einstellung.zurueckNach,
    client_id: anbieter.empfaenger,
  });
  if (einstellung.idToken) frage.set("id_token_hint", einstellung.idToken);
  return `${anbieter.aussteller}/protocol/openid-connect/logout?${frage.toString()}`;
}
